import React, { useState, useEffect, useCallback } from 'react';
import { bridgeApi } from '../api';
import StatusBadge from '../shared/StatusBadge';
import ConfirmDialog from '../shared/ConfirmDialog';

const STRATEGIES = ['direct', 'snn_router', 'fan_out'];
const SEVERITIES = ['', 'info', 'warning', 'critical'];

const EMPTY_ROUTE = {
  workspace_id: '', name: '', description: '', match_entity_type: '',
  match_tags: '', match_severity: '', routing_strategy: 'direct',
  target_process_ids: '', router_graph_id: '', priority: 100, is_active: true,
};

const EMPTY_FEEDBACK = {
  workspace_id: '', source_process_id: '', output_field: '', output_value: '',
  target_process_id: '', description: '', is_active: true,
};

export default function BridgeTab({ workspaceId }) {
  const [mappings, setMappings] = useState([]);
  const [feedbackRoutes, setFeedbackRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [editingMapId, setEditingMapId] = useState(null);
  const [deleteMapId, setDeleteMapId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [editingFbId, setEditingFbId] = useState(null);
  const [deleteFbId, setDeleteFbId] = useState(null);
  const [mapForm, setMapForm] = useState({ ...EMPTY_ROUTE, workspace_id: workspaceId });
  const [fbForm, setFbForm] = useState({ ...EMPTY_FEEDBACK, workspace_id: workspaceId });

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [m, f] = await Promise.all([
        bridgeApi.listRouteMappings(workspaceId),
        bridgeApi.listFeedbackRoutes(workspaceId),
      ]);
      setMappings(Array.isArray(m) ? m : []);
      setFeedbackRoutes(Array.isArray(f) ? f : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleEditMapping = (m) => {
    setMapForm({
      workspace_id: workspaceId,
      name: m.name || '',
      description: m.description || '',
      match_entity_type: m.match_entity_type || '',
      match_tags: Array.isArray(m.match_tags) ? m.match_tags.join(', ') : (m.match_tags || ''),
      match_severity: m.match_severity || '',
      routing_strategy: m.routing_strategy || 'direct',
      target_process_ids: Array.isArray(m.target_process_ids) ? m.target_process_ids.join(', ') : (m.target_process_ids || ''),
      router_graph_id: m.router_graph_id || '',
      priority: m.priority ?? 100,
      is_active: m.is_active !== false,
    });
    setEditingMapId(m.mapping_id);
    setShowMapping(true);
  };

  const handleDeleteMapping = async () => {
    if (!deleteMapId) return;
    try { await bridgeApi.deleteRouteMapping(deleteMapId); setDeleteMapId(null); load(); }
    catch (err) { alert('Delete error: ' + (err.response?.data?.detail || err.message)); setDeleteMapId(null); }
  };

  const handleCreateMapping = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...mapForm,
        workspace_id: workspaceId,
        match_tags: mapForm.match_tags ? mapForm.match_tags.split(',').map(s => s.trim()) : [],
        target_process_ids: mapForm.target_process_ids ? mapForm.target_process_ids.split(',').map(s => s.trim()) : [],
        match_severity: mapForm.match_severity || null,
        router_graph_id: mapForm.router_graph_id || null,
      };
      if (editingMapId) {
        await bridgeApi.updateRouteMapping(editingMapId, payload);
      } else {
        await bridgeApi.createRouteMapping(payload);
      }
      setShowMapping(false);
      setEditingMapId(null);
      setMapForm({ ...EMPTY_ROUTE, workspace_id: workspaceId });
      load();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleEditFeedback = (r) => {
    setFbForm({
      workspace_id: workspaceId,
      source_process_id: r.source_process_id || '',
      output_field: r.output_field || '',
      output_value: r.output_value || '',
      target_process_id: r.target_process_id || '',
      description: r.description || '',
      is_active: r.is_active !== false,
    });
    setEditingFbId(r.route_id);
    setShowFeedback(true);
  };

  const handleDeleteFeedback = async () => {
    if (!deleteFbId) return;
    try { await bridgeApi.deleteFeedbackRoute(deleteFbId); setDeleteFbId(null); load(); }
    catch (err) { alert('Delete error: ' + (err.response?.data?.detail || err.message)); setDeleteFbId(null); }
  };

  const handleCreateFeedback = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...fbForm, workspace_id: workspaceId };
      if (editingFbId) {
        await bridgeApi.updateFeedbackRoute(editingFbId, payload);
      } else {
        await bridgeApi.createFeedbackRoute(payload);
      }
      setShowFeedback(false);
      setEditingFbId(null);
      setFbForm({ ...EMPTY_FEEDBACK, workspace_id: workspaceId });
      load();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const setM = (k, v) => setMapForm(prev => ({ ...prev, [k]: v }));
  const setF = (k, v) => setFbForm(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      {/* Route Mappings */}
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Route Mappings</h2>
          <button className="ednl-btn ednl-btn--primary" onClick={() => { setShowMapping(!showMapping); if (showMapping) { setEditingMapId(null); setMapForm({ ...EMPTY_ROUTE, workspace_id: workspaceId }); } }}>
            {showMapping ? 'Cancel' : '+ New Mapping'}
          </button>
        </div>

        {showMapping && (
          <form className="ednl-form" onSubmit={handleCreateMapping} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Name</label><input className="ednl-input" value={mapForm.name} onChange={e => setM('name', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Strategy</label>
                <select className="ednl-select" value={mapForm.routing_strategy} onChange={e => setM('routing_strategy', e.target.value)}>
                  {STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="ednl-form-group" style={{ maxWidth: 100 }}><label className="ednl-label">Priority</label><input className="ednl-input" type="number" value={mapForm.priority} onChange={e => setM('priority', parseInt(e.target.value))} /></div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Match Entity Type</label><input className="ednl-input" value={mapForm.match_entity_type} onChange={e => setM('match_entity_type', e.target.value)} /></div>
              <div className="ednl-form-group"><label className="ednl-label">Match Tags (comma-separated)</label><input className="ednl-input" value={mapForm.match_tags} onChange={e => setM('match_tags', e.target.value)} /></div>
              <div className="ednl-form-group"><label className="ednl-label">Match Severity</label>
                <select className="ednl-select" value={mapForm.match_severity} onChange={e => setM('match_severity', e.target.value)}>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s || '(any)'}</option>)}
                </select>
              </div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Target Process IDs (comma-separated)</label><input className="ednl-input" value={mapForm.target_process_ids} onChange={e => setM('target_process_ids', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Router Graph ID (for engine routing)</label><input className="ednl-input" value={mapForm.router_graph_id} onChange={e => setM('router_graph_id', e.target.value)} /></div>
            </div>
            <div className="ednl-form-group"><label className="ednl-label">Description</label><input className="ednl-input" value={mapForm.description} onChange={e => setM('description', e.target.value)} /></div>
            <div><button className="ednl-btn ednl-btn--primary" type="submit">{editingMapId ? 'Save Changes' : 'Create Mapping'}</button></div>
          </form>
        )}

        {loading ? <div className="ednl-loading">Loading...</div> : mappings.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__icon">🔀</div><div className="ednl-empty__text">No route mappings configured</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Name</th><th>Strategy</th><th>Entity</th><th>Tags</th><th>Severity</th><th>Targets</th><th>Priority</th><th>Actions</th></tr></thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.mapping_id}>
                    <td>{m.name}</td>
                    <td><StatusBadge status="info" label={m.routing_strategy} /></td>
                    <td>{m.match_entity_type || '*'}</td>
                    <td>{(m.match_tags || []).join(', ') || '—'}</td>
                    <td>{m.match_severity || '—'}</td>
                    <td className="mono">{(m.target_process_ids || []).length} process(es)</td>
                    <td>{m.priority}</td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleEditMapping(m)}>Edit</button>
                        <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => setDeleteMapId(m.mapping_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback Routes */}
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Feedback Routes</h2>
          <button className="ednl-btn ednl-btn--primary" onClick={() => { setShowFeedback(!showFeedback); if (showFeedback) { setEditingFbId(null); setFbForm({ ...EMPTY_FEEDBACK, workspace_id: workspaceId }); } }}>
            {showFeedback ? 'Cancel' : '+ New Feedback Route'}
          </button>
        </div>

        {showFeedback && (
          <form className="ednl-form" onSubmit={handleCreateFeedback} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Source Process ID</label><input className="ednl-input" value={fbForm.source_process_id} onChange={e => setF('source_process_id', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Output Field</label><input className="ednl-input" value={fbForm.output_field} onChange={e => setF('output_field', e.target.value)} required placeholder="recommendation" /></div>
              <div className="ednl-form-group"><label className="ednl-label">Output Value</label><input className="ednl-input" value={fbForm.output_value} onChange={e => setF('output_value', e.target.value)} required placeholder="FILE_SAR" /></div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Target Process ID</label><input className="ednl-input" value={fbForm.target_process_id} onChange={e => setF('target_process_id', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Description</label><input className="ednl-input" value={fbForm.description} onChange={e => setF('description', e.target.value)} /></div>
            </div>
            <div><button className="ednl-btn ednl-btn--primary" type="submit">{editingFbId ? 'Save Changes' : 'Create Feedback Route'}</button></div>
          </form>
        )}

        {feedbackRoutes.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__text">No feedback routes configured</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Source Process</th><th>Output</th><th>Value</th><th>Target Process</th><th>Description</th><th>Actions</th></tr></thead>
              <tbody>
                {feedbackRoutes.map(r => (
                  <tr key={r.route_id}>
                    <td className="mono">{r.source_process_id?.slice(0, 8)}...</td>
                    <td>{r.output_field}</td>
                    <td><StatusBadge status="info" label={r.output_value} /></td>
                    <td className="mono">{r.target_process_id?.slice(0, 8)}...</td>
                    <td>{r.description || '—'}</td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleEditFeedback(r)}>Edit</button>
                        <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => setDeleteFbId(r.route_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleteMapId} title="Delete Route Mapping" message="Permanently delete this route mapping?" onConfirm={handleDeleteMapping} onCancel={() => setDeleteMapId(null)} />
      <ConfirmDialog open={!!deleteFbId} title="Delete Feedback Route" message="Permanently delete this feedback route?" onConfirm={handleDeleteFeedback} onCancel={() => setDeleteFbId(null)} />
    </div>
  );
}
